/**
 * Client-side error reporter. POSTs uncaught crashes to `/api/log-error`,
 * which forwards to the `app-error-log` edge function action.
 *
 * Safety rails:
 *  - Wrapped in try/catch so the reporter itself can never throw.
 *  - Drops events when offline.
 *  - Uses `navigator.sendBeacon` where available so reports survive
 *    page unload (e.g. crash that triggers a navigation away).
 */

export interface ReportableError {
  message: string;
  stack?: string | null;
  errorType?: string | null;
  source?: "client" | "server";
  url?: string | null;
  route?: string | null;
  context?: Record<string, unknown> | null;
}

/** Endpoint we POST to. Same-origin server route. */
const ENDPOINT = "/api/log-error";

/** A simple in-memory ring to suppress duplicate reports within a session.
 *  Server-side fingerprinting still does the heavy lifting; this is just
 *  to avoid hammering the network when the same uncaught error fires 200
 *  times in a tight loop. */
const recentlySeen = new Set<string>();
const RECENT_LIMIT = 50;

/**
 * Errors we never want logged. These are not actionable from app code:
 * they originate in framework / browser / extension layers and only
 * pollute the dashboard.
 *
 * `$RS` — Next.js streaming-Suspense helper emitted inline in the HTML.
 * Does `document.getElementById(a).parentNode.removeChild(a)`; throws
 * if a browser extension (Grammarly, Dark Reader, ad blockers, Google
 * Translate) or fast-nav has already stripped the placeholder
 * <template>. Harmless — the streamed chunk either landed or the user
 * moved on.
 */
function isIgnorable(payload: ReportableError): boolean {
  const stack = payload.stack ?? "";
  const msg = payload.message ?? "";
  if (stack.includes("$RS") && msg.includes("parentNode")) return true;
  // ResizeObserver loop noise — Chrome fires this on layout thrash;
  // not actionable, never user-visible.
  if (msg.includes("ResizeObserver loop")) return true;
  /* React #418 — hydration text mismatch — is SELF-HEALING: React discards the
     server text for that subtree and re-renders it on the client, so the user
     sees the right value and the page keeps working. It fired repeatedly across
     unrelated pages (/karley, /amanda, /capacity-v2, /claims…) from
     independent timezone/clock causes, each time reading as "another error"
     while nothing was actually broken. Muted app-wide on Robert's call
     (2026-07-15).

     Scope is deliberately EXACTLY #418 (text-content mismatch), not the whole
     hydration family: #423/#425 and friends can mean a subtree failed to
     hydrate at all, which is worth hearing about. The trade we accept: #418 was
     what surfaced the daysUntil bug, so we lose that weak early-warning — but a
     mismatch that DOESN'T self-heal shows up as a visibly wrong value a human
     reports, and this line is one edit away from reverting. */
  if (msg.includes("Minified React error #418") || msg.includes("error #418")) return true;
  return false;
}

function dedupKey(payload: ReportableError): string {
  return [
    payload.source ?? "client",
    payload.errorType ?? "",
    (payload.stack ?? payload.message ?? "").split("\n", 1)[0],
    payload.route ?? "",
  ].join("|");
}

export function reportClientError(input: unknown, extras: Partial<ReportableError> = {}): void {
  try {
    if (typeof window === "undefined") return;
    if (navigator && navigator.onLine === false) return;

    const payload = normalize(input, extras);
    if (!payload) return;
    if (isIgnorable(payload)) return;

    const key = dedupKey(payload);
    if (recentlySeen.has(key)) return;
    recentlySeen.add(key);
    if (recentlySeen.size > RECENT_LIMIT) {
      // Drop the oldest by reissuing the set — simple, no LRU needed.
      const first = recentlySeen.values().next().value as string | undefined;
      if (first !== undefined) recentlySeen.delete(first);
    }

    const body = JSON.stringify({
      source: payload.source ?? "client",
      error_type: payload.errorType ?? null,
      message: payload.message,
      stack: payload.stack ?? null,
      url: payload.url ?? window.location.href,
      route: payload.route ?? routeFromPath(window.location.pathname),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      context: payload.context ?? null,
    });

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([body], { type: "application/json" });
        const queued = navigator.sendBeacon(ENDPOINT, blob);
        if (queued) return;
      } catch {
        // Fall through to fetch.
      }
    }

    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      // Never throw from the reporter — silent drop.
    });
  } catch {
    // Reporter must never crash. Silent drop.
  }
}

function normalize(
  input: unknown,
  extras: Partial<ReportableError>,
): ReportableError | null {
  if (input && typeof input === "object" && "message" in (input as object)) {
    const err = input as Error;
    return {
      message: String(err.message || extras.message || "(no message)"),
      stack: err.stack ?? extras.stack ?? null,
      errorType: err.name || extras.errorType || "Error",
      source: extras.source ?? "client",
      url: extras.url,
      route: extras.route,
      context: extras.context,
    };
  }
  if (typeof input === "string") {
    return {
      message: input || extras.message || "(no message)",
      stack: extras.stack ?? null,
      errorType: extras.errorType ?? "Error",
      source: extras.source ?? "client",
      url: extras.url,
      route: extras.route,
      context: extras.context,
    };
  }
  if (extras.message) {
    return {
      message: extras.message,
      stack: extras.stack ?? null,
      errorType: extras.errorType ?? "Error",
      source: extras.source ?? "client",
      url: extras.url,
      route: extras.route,
      context: extras.context,
    };
  }
  return null;
}

/**
 * Collapse a pathname like /contacts/abc123 to /contacts/[id] for grouping.
 * This is best-effort — exact route patterns would require AppRouter
 * internals which aren't reliably available client-side.
 */
function routeFromPath(pathname: string): string {
  return pathname
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      // UUID-ish, numeric, or 16+ char hex → [id]
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(seg)) return "[id]";
      if (/^\d+$/.test(seg)) return "[id]";
      if (/^[0-9a-f]{16,}$/i.test(seg)) return "[id]";
      // Long path tokens that look like emails or hashes
      if (seg.includes("@")) return "[email]";
      return seg;
    })
    .join("/");
}
